import { useState } from "react";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { PRICING } from "@/data/content";

const Pricing = () => {
    const [currency, setCurrency] = useState("inr");

    return (
        <section id="pricing" className="bg-sand py-24 lg:py-32" data-testid="pricing-section">
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <SectionHeading
                    align="center"
                    eyebrow="Simple Pricing"
                    title="World-class teaching, honestly priced"
                    copy="Every plan starts with a free trial lesson. No contracts, no card up front — cancel any time."
                />
                <Reveal delay={0.1} className="mt-10 flex justify-center">
                    <div className="inline-flex flex-wrap justify-center rounded-full border border-border bg-cream p-1 card-shadow" data-testid="currency-toggle">
                        {[["inr", "₹ INR"], ["eur", "€ EUR"], ["usd", "$ USD"], ["aud", "A$ AUD"], ["cad", "C$ CAD"]].map(([key, label]) => (
                            <button
                                key={key}
                                type="button"
                                data-testid={`currency-toggle-${key}`}
                                onClick={() => setCurrency(key)}
                                className={`rounded-full px-6 py-2 text-sm font-medium transition-all duration-300 ${
                                    currency === key ? "bg-teal text-cream" : "text-ink/70 hover:text-ink"
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </Reveal>
                <div className="mt-12 grid gap-5 lg:grid-cols-3" data-testid="pricing-grid">
                    {PRICING.map((p, i) => (
                        <Reveal key={p.name} delay={i * 0.1}>
                            <article
                                className={`relative flex h-full flex-col rounded-3xl border p-8 transition-all duration-300 hover:-translate-y-1.5 hover:lift-shadow lg:p-10 ${
                                    p.featured ? "border-saffron bg-teal text-cream lift-shadow" : "border-border bg-cream card-shadow"
                                }`}
                                data-testid={`pricing-card-${p.name.toLowerCase().replace(/\s/g, "-")}`}
                            >
                                {p.featured && (
                                    <span className="absolute -top-3.5 left-8 rounded-full bg-saffron px-4 py-1.5 text-xs font-medium uppercase tracking-[0.14em] text-cream" data-testid="pricing-featured-badge">
                                        Most popular
                                    </span>
                                )}
                                <h3 className={`font-serif text-xl font-semibold md:text-2xl ${p.featured ? "text-cream" : "text-ink"}`}>{p.name}</h3>
                                <p className={`mt-2 text-sm leading-relaxed ${p.featured ? "text-cream/70" : "text-muted-foreground"}`}>{p.description}</p>
                                <p className="mt-6" data-testid={`pricing-price-${p.name.toLowerCase().replace(/\s/g, "-")}`}>
                                    <span className={`font-serif text-5xl font-semibold tracking-tight ${p.featured ? "text-saffron" : "text-teal"}`}>
                                        {p[currency]}
                                    </span>
                                    <span className={`ml-2 text-sm ${p.featured ? "text-cream/60" : "text-muted-foreground"}`}>{p.unit}</span>
                                </p>
                                <ul className="mt-7 flex-1 space-y-3">
                                    {p.features.map((f) => (
                                        <li key={f} className={`flex items-start gap-3 text-sm ${p.featured ? "text-cream/85" : "text-ink/80"}`}>
                                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-saffron" />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to="/free-trial"
                                    data-testid={`pricing-cta-${p.name.toLowerCase().replace(/\s/g, "-")}`}
                                    className={`mt-8 rounded-full px-6 py-3.5 text-center text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 ${
                                        p.featured
                                            ? "bg-saffron text-cream hover:bg-saffron-dark"
                                            : "border border-teal/30 text-teal hover:bg-teal hover:text-cream"
                                    }`}
                                >
                                    Start with a Free Lesson
                                </Link>
                            </article>
                        </Reveal>
                    ))}
                </div>
                <p className="mt-8 text-center text-xs text-muted-foreground" data-testid="pricing-currency-note">
                    Prices shown in Rupees, Euros, US, Australian or Canadian Dollars — switch above. Other currencies converted at checkout.
                </p>
            </div>
        </section>
    );
};
export default Pricing;
